import React, { useMemo } from "react";
import styled from "styled-components";
import { NVStackedAreaChartDownsampled } from "common/graphs/DownSampling";
import {
  tooltipContentGeneratorForUnits,
  formats,
} from "common/graphs/NVStackedAreaChart";
import { PERIOD_DASHBOARD_TO_HOURS, groupIntoRest } from "./utils";
import useDashboardData from "./useDashboardData";
import { safeStr, atBqnTarget } from "common/api";
import Loading from "common/Loading";
import { parsePoliciesThroughputIgnoringSpeeds } from "views/Statistics/Subscribers/PerPolicy/api";
import colors from "common/graphs/colors";
import colorsDark from "common/graphs/colorsDark";
import * as d3 from "d3";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 270px;
  color: var(--text-primary);
`;

const getSubscriberPerPolicyData = ({ hours, mode, bqnId, type, range }) =>
  ifCl
    .run(
      atBqnTarget(
        `show statistics policy ${safeStr(type)} speed lines ${hours * 12}`,
        mode,
        bqnId,
      ),
    )
    .then((response) => ({
      data: parsePoliciesThroughputIgnoringSpeeds(response),
      range,
    }));

const chartTooltipGenerator = tooltipContentGeneratorForUnits(
  "Subscribers",
  formats.noDecimals,
  true,
  false,
);

function SubscribersPerPolicy({ type, period, range }) {
  const colorsSet = login.isTheme("light") ? colors : colorsDark;

  // The range of the dashboard is the clock of the chart, which is asked for
  // again with each new one of them.
  const { data: dataRange, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId, range }) =>
      getSubscriberPerPolicyData({
        hours: PERIOD_DASHBOARD_TO_HOURS[period],
        mode,
        bqnId,
        type,
        range,
      }),
    onError: "Error fetching subscribers per policy data:",
    deps: [period, type],
    range,
  });

  const { data, range: rangeDrawn } = dataRange || {};
  const { timeline, participants } = data || {};

  const parsedItems = useMemo(() => {
    return timeline
      ? timeline.map((item) => {
          const policies = item.policies.reduce((acc, policy) => {
            acc[policy.name] = policy.subscribers;
            return acc;
          }, {});
          return {
            time: item.time,
            ...policies,
          };
        })
      : [];
  }, [timeline]);

  // A BQN of many policies would fill the card with the legend of the chart,
  // and its tooltip with a row per policy, so the ones left over the items
  // the legend holds are drawn as one area of their own, which adds up the
  // subscribers of all of them. Both the legend and the tooltip name the
  // areas the chart is given, so neither of them shows more.
  const { participants: policiesShown, items: itemsShown } = useMemo(
    () => groupIntoRest(participants || [], parsedItems, 9),
    [participants, parsedItems]
  );

  const fields = useMemo(() => {
    return policiesShown.map((p, i) => ({
      name: p,
      label: p,
      color: colorsSet[i % colorsSet.length],
    }));
  }, [policiesShown, colorsSet]);

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!timeline || timeline.length === 0) {
    return <WrapperLoader>No data available</WrapperLoader>;
  }

  return (
    <Wrapper>
      <NVStackedAreaChartDownsampled
        copyEndpoints={true}
        xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
        xField="time"
        fields={fields}
        items={itemsShown}
        range={rangeDrawn}
        yAxisUnits={"Subscribers"}
        yAxisFormat={formats.noDecimals}
        tooltipContentGenerator={chartTooltipGenerator}
        yAxisTopGap={0.05}
        margin={{ top: 5, right: 10, bottom: 20, left: 60 }}
        xAxisTicksNumber={4}
      />
    </Wrapper>
  );
}

export default React.memo(SubscribersPerPolicy);
